import { useCallback, useEffect, useRef, useState } from 'react';
import { useDataVersion } from '@/state/dataVersion';

/**
 * Async data hook with explicit loading / error / retry states.
 *
 * Every list and detail screen in the package must render loading, populated,
 * empty and recoverable-error states. Funnelling them through one hook keeps
 * that consistent and makes the states testable.
 */

export type AsyncState<T> =
  | { readonly status: 'loading'; readonly data: T | null; readonly error: null }
  | { readonly status: 'success'; readonly data: T; readonly error: null }
  | { readonly status: 'error'; readonly data: T | null; readonly error: unknown };

export type UseAsyncResult<T> = AsyncState<T> & {
  readonly reload: () => void;
  /** Replace data locally after a mutation, without a refetch. */
  readonly setData: (next: T) => void;
};

export function useAsync<T>(fn: () => Promise<T>, deps: readonly unknown[]): UseAsyncResult<T> {
  const [state, setState] = useState<AsyncState<T>>({
    status: 'loading',
    data: null,
    error: null,
  });
  const [nonce, setNonce] = useState(0);
  // Any change to the underlying fixtures or scenario re-runs every query.
  const dataVersion = useDataVersion((s) => s.version);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ status: 'loading', data: prev.data, error: null }));
    fnRef
      .current()
      .then((data) => {
        if (!cancelled) setState({ status: 'success', data, error: null });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState((prev) => ({ status: 'error', data: prev.data, error }));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, dataVersion]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const setData = useCallback(
    (next: T) => setState({ status: 'success', data: next, error: null }),
    [],
  );

  return { ...state, reload, setData };
}

/**
 * Outcome of a mutation, returned synchronously from `run`.
 *
 * React state updates are asynchronous, so a caller that inspected
 * `mutation.error` immediately after awaiting `run` would read the previous
 * render's value and silently skip its error handling. Returning the outcome
 * removes that whole class of bug.
 */
export type MutationOutcome<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: unknown }
  /** A second submit arrived while the first was still in flight. */
  | { readonly ok: false; readonly error: null; readonly duplicate: true };

/**
 * Mutation hook with duplicate-submit protection.
 *
 * While a mutation is in flight the trigger is disabled, satisfying
 * "preventing double-submit while preserving retry" — a failed mutation
 * returns to idle so the user may deliberately try again.
 */
export function useMutation<TArgs extends readonly unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
): {
  readonly run: (...args: TArgs) => Promise<MutationOutcome<TResult>>;
  readonly pending: boolean;
  readonly error: unknown;
  readonly reset: () => void;
} {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const inFlight = useRef(false);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const run = useCallback(async (...args: TArgs): Promise<MutationOutcome<TResult>> => {
    if (inFlight.current) return { ok: false, error: null, duplicate: true };
    inFlight.current = true;
    setPending(true);
    setError(null);
    try {
      const value = await fnRef.current(...args);
      return { ok: true, value };
    } catch (e) {
      setError(e);
      return { ok: false, error: e };
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }, []);

  const reset = useCallback(() => setError(null), []);

  return { run, pending, error, reset };
}

/** Reads the user's reduced-motion preference reactively. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  );
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return undefined;
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return reduced;
}
